import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId, scrollToTop } from "../../lib/scroll";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Expertise", id: "capabilities" },
  { label: "Process", id: "process" },
  { label: "Founder", id: "founder" },
  { label: "Contact", id: "contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.19, 1, 0.22, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-ink/70 backdrop-blur-xl border-b border-hairline" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12 h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={scrollToTop} className="flex items-baseline gap-2 group">
          <span className="font-serif text-2xl tracking-tight text-parchment">EQ <span className="text-gold">Capital</span></span>
          <span className="hidden sm:inline font-mono text-[10px] tracking-[0.25em] text-stone uppercase">Powered by GT Securities</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono text-xs tracking-[0.2em] uppercase text-stone hover:text-parchment link-underline transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => go("contact")}
            className="font-mono text-xs tracking-[0.2em] uppercase border border-gold text-gold px-6 py-3 hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            Get Started
          </button>
        </nav>

        <button data-testid="nav-menu-toggle" className="md:hidden text-parchment" onClick={() => setOpen((v) => !v)}>
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.19, 1, 0.22, 1] }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-hairline"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button key={l.id} data-testid={`nav-mobile-${l.id}`} onClick={() => go(l.id)}
                  className="text-left font-serif text-2xl text-parchment">{l.label}</button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
